import React, { useState, useCallback, useMemo } from 'react';
import moment from 'moment';
import { Form, Popup } from 'semantic-ui-react';
import { Reports } from '../../../api/services/next';
import { useMessageResources, useTextResources } from '../../../language';
import GridSearchTemplate from '../../../pages/GridSearchTemplate';
import CheckboxWrapper from '../../shared/Checkbox';
import { DatePicker } from '../../shared/DatePicker';
import HotelsDropDown from '../../shared/HotelsDropDown';
import RatesDropDown from '../../shared/RatesDropDown';
import RoomTypesDropDown from '../../shared/RoomTypesDropDown';
import { onWarning } from '../../shared/messages';
import {
  formatValueAsCurrency,
  formatDate
} from '../../shared/utils';
import { endpoint } from "../../../api/http"
import CustomerDropDown from '../../shared/CustomerDropDown';
import PricingTemplatesDropDown from '../../shared/PriceGroupsDropDown';

const FinalPricesByCombination = () => {
  const text = useTextResources();
  const [data, setData] = useState();
  const [hotelId, setHotelId] = useState();
  const [datesRange, setDatesRange] = useState();
  const getDatesRangeFilter = () => {
    return (
      datesRange && [
        moment(datesRange[0]).toISOString(),
        moment(datesRange[1]).toISOString()
      ]
    );
  }

  const isDayUse = () => {
    return datesRange && moment(datesRange[0]).format('YYYY-MM-DD') == moment(datesRange[1]).format('YYYY-MM-DD');
  }
  const byHotelSpec = useMemo(
    () => ({ HotelId: hotelId }),
    [hotelId]
  );

  const msg = useMessageResources();
  const getDate = useCallback((row, columnName) => {
    const value = row[columnName];
    return value ? formatDate(value) : '-';
  }, []);

  const rateNameformatter = ({ value }) => {
    let productName = data && data.find(r => r.RateId === value)?.RateName || '';

    return (
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${endpoint}PMS/pricelist/Edit?RateId=${value}`}
      >
        {productName}
      </a>
    );
  };

  const productNameformatter = ({ value }) => {
    return data && data.find(r => r.ProductId === value)?.ProductName || '';
  };

  const getTemplateNameCellValue = useCallback((row) => {
    const templateName = row?.TemplateName;
    return templateName ?? '-';
  }, []);

  const getFormattedCurrencyOrPercentCellValue = useCallback((row, columnName) => {
    const value = row[columnName];
    if (value === undefined) {
      return '-';
    }
    return row.IsPercent
      ? `${value}%`
      : formatValueAsCurrency(value, row.CurrencyId);
  }, []);

  const toolTipFormatter = (value) => {
    return <Popup content={value} trigger={<span>{value}</span>} />;
  };

  const onSearchComplete = useCallback(async (data) => {
    setData(data);
  }, []);

  const customizeFooter = worksheet => {
    const rows = worksheet._rows.filter((value, index) => index === 0 || value._outlineLevel === 2);
    worksheet._rows = [];
    rows.forEach((row) => worksheet.addRow(row.values));
  }

  const columns = useMemo(() => [
    {
      name: 'RateId',
      title: text('Rate'),
      formatter: rateNameformatter,
    },
    {
      name: 'RateName',
      title: text('RateName'),
    },
    {
      name: 'ProductId',
      title: text('Product'),
      formatter: productNameformatter,
    },
    {
      name: 'ProductName',
      title: text('ProductName'),
    },
    {
      name: "Date",
      title: text("Date"),
      getCellValue: getDate
    },
    {
      name: 'PricingTemplate',
      title: text('PricingTemplate'),
      getCellValue: getTemplateNameCellValue,
    },
    {
      name: 'Single',
      title: text('Single'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'Double',
      title: text('Double'),
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultZeroChildOne',
      title: 'A0, C1',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultZeroChildTwo',
      title: 'A0, C2',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultZeroChildThree',
      title: 'A0, C3',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultZeroChildFour',
      title: 'A0, C4',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultZeroChildFive',
      title: 'A0, C5',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultOneChildOne',
      title: 'A1, C1',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultOneChildTwo',
      title: 'A1, C2',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultOneChildThree',
      title: 'A1, C3',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultOneChildFour',
      title: 'A1, C4',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
    {
      name: 'AdultOneChildFive',
      title: 'A1, C5',
      getCellValue: getFormattedCurrencyOrPercentCellValue,
    },
  ].map(c => ({ ...c, exportTitle: c.title, title: toolTipFormatter(c.title) })),
    [getFormattedCurrencyOrPercentCellValue, toolTipFormatter, text]);

  const gridOptions = {
    search: {
      title: text('FinalPricesByCombination'),
      fields: [
        <Form.Field
          key='HotelId'
          label={text('Hotel')}
          onChange={setHotelId}
          control={HotelsDropDown}
          placeholder={text('SelectHotel')}
          required
        />,
        <DatePicker
          picktype='range'
          label={text('Dates')}
          key='FromToDates'
          onChange={setDatesRange}
          clearable
        />,
        <Form.Field
          label={text("RoomTypes")}
          placeholder={text("Select")}
          control={RoomTypesDropDown}
          multiple
          search
          spec={byHotelSpec}
          key='RoomTypeIds'
          width='3'
          clearable
        />,
        <Form.Field
          label={text('PricingTemplates')}
          control={PricingTemplatesDropDown}
          key='PricingTemplateIds'
          placeholder={text('All')}
          clearable
          width='3'
          multiple
          search
        />,
        <Form.Field
          label={text('Rates')}
          control={RatesDropDown}
          key='RateIds'
          spec={byHotelSpec}
          placeholder={text('All')}
          clearable
          width='3'
          multiple
          search
        />,
        <Form.Field
          label={text('Customer')}
          control={CustomerDropDown}
          key='CustomerId'
          placeholder={text('All')}
          clearable
          width='3'
          search
        />,
        <CheckboxWrapper label={text('IncludeVAT')} key='PriceIncludesVat' />,
      ],
      method: 'GET',
      api: Reports.FinalPricesByCombination,
      spec: {
        FromToDates: getDatesRangeFilter(),
        GetPricingForLastDateOfRange: isDayUse(),
      },
      validate: (state) => {
        let fromDate = state['FromToDates[0]'];
        let toDate = state['FromToDates[1]'];

        if (!fromDate) onWarning(msg('FromDateIsRequired'));
        if (!toDate) onWarning(msg('ToDateIsRequired'));
        return Boolean(fromDate && toDate);
      },
    },
    grid: {
      columns,
      noVirtualTable: true,
      paging: true,
      customOptions: {
        overrideData: true,
        group: {
          grouping: [
            { columnName: 'RateId' },
            { columnName: 'ProductId' }
          ]
        },
        hidden: {
          hiddenColumns: [
            { columnName: 'RateName' },
            { columnName: 'ProductName' }
          ],
        }
      },
      summary: true,
      scrollbarX: true,
      filtering: {},
      export: true,
      excel: {
        customizeFooter: customizeFooter,
      },
    },
  };

  return (
    <div style={{ 'overflowX': 'auto' }} >
      <GridSearchTemplate
        options={gridOptions} onSearchComplete={onSearchComplete} data={data}
      />
    </div >
  );
};

export default FinalPricesByCombination;
